/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

interface AuthFetchTrustedServicesStore {
  /** The token, regularly updated with the new refreshed token. */
  access_token?: string;
  /**
   * Lowercased hostnames (no scheme, path or query) that are allowed to
   * receive the access_token. Matched against URL.hostname only.
   */
  trustedHosts: Set<string>;
}

const store: AuthFetchTrustedServicesStore = {
  access_token: undefined,
  trustedHosts: new Set<string>(),
};

export const setJWTAccessTokenForTrustedServices = (
  access_token: string | undefined,
): void => {
  store.access_token = access_token;
};

/**
 * Resets the list of trusted services
 */
export const resetAuthFetchTrustedServices = (): void => {
  store.trustedHosts.clear();
};
/**
 * A list of hostnames (e.g. "adaguc-server-geoweb.geoweb.knmi.cloud") that
 * are allowed to receive the JWT access_token.
 * @param hosts
 * @param enable
 */
export const enableAuthFetchTrustedServices = (
  hosts: string[],
  enable = true,
): void => {
  hosts.forEach((host) => {
    const normalizedHost = host.toLowerCase();
    if (enable) {
      store.trustedHosts.add(normalizedHost);
    } else {
      store.trustedHosts.delete(normalizedHost);
    }
  });
};

/**
 * Extend the headers with an Authorization header containing the JWT access_token.
 * @param fetchHeaders Incoming Headers, will be extended with an Authorization header.
 * @param url Incoming url, whose hostname is checked (case-insensitively) against the list of hosts set by enableAuthFetchTrustedServices.
 * @returns
 */
export const extendHeadersWithJWTAccessToken = (
  fetchHeaders: Headers,
  url: string | URL | Request,
): Headers => {
  if (!store.access_token) {
    return fetchHeaders;
  }
  try {
    const newUrl = (url as Request).url || url;
    const { hostname } = new URL(newUrl.toString());
    if (store.trustedHosts.has(hostname.toLowerCase())) {
      fetchHeaders.append('Authorization', `Bearer ${store.access_token}`);
    }
  } catch (_e) {
    /* empty */
  }
  return fetchHeaders;
};

/**
 * Similar to fetch, but when trusted it adds an Authorization header with the JWT access_token. The trusted urls can be set as regexp in enableAuthFetchTrustedServices.
 * @param url
 * @param init
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/promise-function-async, @typescript-eslint/explicit-function-return-type, @typescript-eslint/explicit-module-boundary-types
export const authFetch = (url: string | URL | Request, init?: RequestInit) =>
  fetch(url, {
    ...(init || {}),
    headers: extendHeadersWithJWTAccessToken(new Headers(init?.headers), url),
  });
